import { Controller } from "@hotwired/stimulus"

/* Usage
 * Controller is attached to a parent UI element which should contain the given targets
 *
 * Sample HTML:
 * <div data-controller="password-strength">
 *  <input type="password" data-password-strength-target="input" data-action="input->password-strength#updateMeter" name="password"></input>
 *  <div>
 *   <progress id="strength-meter" data-password-strength-target="meter" value="0" max="4"></progress>
 *   <label for="strength-meter" data-password-strength-target="label">Strength: Invalid</label>
 *  </div>
 * </div>
 *
 * The meter has 4 steps, 0 through 4.
 */

// Connects to data-controller="password-strength"
export default class extends Controller {
  static targets = ["input", "label", "meter"]

  connect() {
    if (this.hasMeterTarget) {
      this.meterTarget.max = 4;
    }

  }

  updateMeter() {
    if (!this.hasInputTarget) {
      return;
    }

    const password = this.inputTarget.value;
    
    // Handle minimum length requirement first
    if (password.length < 8) {
      this.updateMeterDisplay(0, "Invalid", "bar-invalid");
      return;
    }
    
    // Create the set and convert to array once
    const uniqueChars = Array.from(new Set(password));
    
    // Check which character types are present using the array
    const hasLower = uniqueChars.some(char => /[a-z]/.test(char));
    const hasUpper = uniqueChars.some(char => /[A-Z]/.test(char));
    const hasNumbers = uniqueChars.some(char => /[0-9]/.test(char));
    const hasSymbols = uniqueChars.some(char => /[^a-zA-Z0-9]/.test(char));
    
    // Calculate total possible character set size
    let charsetSize = 0;
    if (hasLower) charsetSize += 26;    // a-z
    if (hasUpper) charsetSize += 26;    // A-Z
    if (hasNumbers) charsetSize += 10;  // 0-9
    if (hasSymbols) charsetSize += 33;  // Common symbols
    
    if (charsetSize === 0) {
      this.updateMeterDisplay(0, "Invalid", "bar-invalid");
      return;
    }
    
    // Calculate entropy using the formula: log2(charsetSize^passwordLength)
    const entropy = Math.log2(Math.pow(charsetSize, password.length));
    
    // Classification based on NordVPN's entropy bits table
    let meterValue, classification, barColor;
    
    if (entropy <= 35) {
      [meterValue, classification, barColor] = [1, "Very Weak", "bar-very-weak"];
    } else if (entropy <= 59) {
      [meterValue, classification, barColor] = [2, "Weak", "bar-weak"];
    } else if (entropy <= 119) {
      [meterValue, classification, barColor] = [3, "Strong", "bar-strong"];
    } else {
      [meterValue, classification, barColor] = [4, "Very Strong", "bar-strong"];
    }
    
    this.updateMeterDisplay(meterValue, classification, barColor);
  }
  
  updateMeterDisplay(value, classification, barColor) {
    if (this.hasMeterTarget) {
      this.meterTarget.value = value;
      this.meterTarget.classList.remove("bar-invalid", "bar-very-weak", "bar-weak", "bar-strong");
      if (barColor) {
        this.meterTarget.classList.add(barColor);
      }
      this.labelTarget.textContent = classification;
    }
  }
}